import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, TrendingUp, Zap, Landmark, BadgeCheck, Settings, CheckCircle2 } from 'lucide-react';
import { runSolarAssessment, formatIndianCurrency, SOLAR_CONFIG } from './solarUtils';

const SolarCalculator = () => {
  const [inputMode, setInputMode] = useState('bill'); // 'bill' or 'units'
  const [inputValue, setInputValue] = useState(3000); // Default ₹3000 or 400 units
  const [propertyType, setPropertyType] = useState('residential');

  // Trigger Master Engine on change hooks
  const metrics = runSolarAssessment(inputMode, inputValue, propertyType);

  // Dynamic label configs based on current state toggles
  const maxRange = inputMode === 'bill' ? 25000 : 15000;
  const minRange = inputMode === 'bill' ? 500 : 50;
  const rangeStep = inputMode === 'bill' ? 250 : 25;

  return (
    <section id="calculator" className="pt-24 pb-12 sm:pt-32 sm:pb-20 bg-[#F4F7FA] relative overflow-hidden border-y border-[#1E5CB3]/10 font-sans">
      
      {/* 🌟 Background Ambient Glows 🌟 */}
      <div className="absolute top-0 right-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#0D3F7F]/5 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#F7941D]/5 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none" />
      
      {/* Subtle Matrix Grid */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: `radial-gradient(#0D3F7F 1px, transparent 1px)`, backgroundSize: '24px 24px' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* 🌟 1. COMPACT SECTION HEADER 🌟 */}
        <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-14">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-[#F0F7FF] border border-[#1E5CB3]/20 px-3.5 py-1 sm:py-1.5 rounded-full mb-3 shadow-sm"
          >
            <Settings className="w-3.5 h-3.5 text-[#F7941D]" />
            <span className="text-[9px] sm:text-[10px] font-bold tracking-widest text-[#0D3F7F] uppercase">
              Grid Generation Engine
            </span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-2xl sm:text-3xl md:text-4xl font-black text-[#0F172A] tracking-tight leading-tight"
          >
            Solar Yield & <span className="bg-gradient-to-r from-[#F7941D] to-[#FFC107] bg-clip-text text-transparent drop-shadow-sm">ROI Calculator</span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-3 text-slate-500 text-xs sm:text-sm md:text-[14px] font-medium leading-relaxed px-2 sm:px-0"
          >
            Sized realistically with true energy consumption rules (Units/₹) matching regional state subsidy regulations.
          </motion.p>
        </div>

        {/* 🌟 2. MAIN CONTROLS & RESULTS GRID 🌟 */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-10 items-stretch">
          
          {/* ⚡ LEFT PANEL: DATA INPUT STACKS */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-5 bg-white border border-slate-200/80 rounded-2xl sm:rounded-[1.5rem] p-5 sm:p-8 shadow-[0_4px_20px_rgba(13,63,127,0.03)] flex flex-col justify-between space-y-5 sm:space-y-6 relative"
          >
            <div className="space-y-5 sm:space-y-6">
              
              {/* Sector Selection (Chips) */}
              <div>
                <label className="block text-[9px] sm:text-[10px] uppercase font-black tracking-widest text-slate-400 mb-2.5">
                  1. Select Property Type
                </label>
                <div className="grid grid-cols-3 gap-2 bg-[#F8FAFC] p-1.5 rounded-xl border border-slate-200/60">
                  {['residential', 'commercial', 'industrial'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        setPropertyType(type);
                        setInputValue(type === 'residential' ? 3000 : 12000);
                      }}
                      className={`py-2.5 rounded-lg font-bold text-[10px] sm:text-xs capitalize tracking-wide transition-all outline-none touch-manipulation ${
                        propertyType === type
                          ? 'bg-white text-[#0D3F7F] shadow-sm border border-slate-200'
                          : 'text-slate-500 hover:text-slate-800'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mode Selection Tabs */}
              <div>
                <label className="block text-[9px] sm:text-[10px] uppercase font-black tracking-widest text-slate-400 mb-2.5">
                  2. Choose Input Mode
                </label>
                <div className="flex gap-2.5 sm:gap-3">
                  <button
                    onClick={() => { setInputMode('bill'); setInputValue(3000); }}
                    className={`flex-1 py-3 rounded-xl font-bold text-[11px] sm:text-[12px] tracking-wide transition-all outline-none border touch-manipulation ${
                      inputMode === 'bill'
                        ? 'border-[#F7941D] bg-[#F7941D]/10 text-[#F7941D]'
                        : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50'
                    }`}
                  >
                    Monthly Bill (₹)
                  </button>
                  <button
                    onClick={() => { setInputMode('units'); setInputValue(400); }}
                    className={`flex-1 py-3 rounded-xl font-bold text-[11px] sm:text-[12px] tracking-wide transition-all outline-none border touch-manipulation ${
                      inputMode === 'units'
                        ? 'border-[#F7941D] bg-[#F7941D]/10 text-[#F7941D]'
                        : 'border-slate-200 text-slate-600 bg-white hover:bg-slate-50'
                    }`}
                  >
                    Monthly Units
                  </button>
                </div>
              </div>

              {/* Advanced Slider Controller */}
              <div className="pt-2">
                <div className="flex justify-between items-end mb-4 gap-3">
                  <label className="text-[9px] sm:text-[10px] uppercase font-black tracking-widest text-slate-400">
                    {inputMode === 'bill' ? 'Average Bill Input' : 'Average Consumption'}
                  </label>
                  <span className="text-2xl sm:text-3xl font-black text-[#0D3F7F] tracking-tight leading-none">
                    {inputMode === 'bill' ? `₹${inputValue.toLocaleString('en-IN')}` : `${inputValue} U`}
                  </span>
                </div>
                
                <div className="relative flex items-center h-8">
                  <input
                    type="range" min={minRange} max={maxRange} step={rangeStep} value={inputValue}
                    onChange={(e) => setInputValue(Number(e.target.value))}
                    className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer touch-manipulation"
                    style={{
                      background: `linear-gradient(to right, #F7941D 0%, #FFC107 ${((inputValue - minRange) / (maxRange - minRange)) * 100}%, #E2E8F0 ${((inputValue - minRange) / (maxRange - minRange)) * 100}%, #E2E8F0 100%)`
                    }}
                  />
                </div>
                
                <div className="flex justify-between text-[9px] sm:text-[10px] text-slate-400 font-bold pt-1.5">
                  <span>MIN: {inputMode === 'bill' ? '₹500' : '50 U'}</span>
                  <span>MAX: {inputMode === 'bill' ? '₹25,000' : '15,000 U'}</span>
                </div>
              </div>
            </div>

            {/* Dynamic Meta Card Descriptor */}
            <div className="mt-4 sm:mt-6 p-3.5 sm:p-4 bg-[#F0F7FF] rounded-xl border border-[#1E5CB3]/10 space-y-2 text-[11px] sm:text-[12px]">
              <div className="flex justify-between items-center text-slate-500 font-medium gap-2">
                <span>State Unit Rate Config:</span>
                <strong className="text-[#0D3F7F] font-bold whitespace-nowrap">₹{SOLAR_CONFIG?.rates?.[propertyType] || 7.5}/Unit</strong>
              </div>
              <div className="flex justify-between items-center text-slate-500 font-medium border-t border-[#1E5CB3]/10 pt-2 gap-2">
                <span>Calculated Load Matrix:</span>
                <strong className="text-[#0D3F7F] font-bold whitespace-nowrap">{metrics.monthlyConsumption} kWh / Month</strong>
              </div>
            </div>
          </motion.div>

          {/* ⚡ RIGHT PANEL: DYNAMIC ROI METRICS */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-7 bg-[#050B14] rounded-2xl sm:rounded-[1.5rem] p-5 sm:p-8 text-white flex flex-col justify-between shadow-[0_20px_40px_rgba(0,0,0,0.2)] relative overflow-hidden border border-[#1E5CB3]/30"
          >
            {/* Ambient Inner Glows */}
            <div className="absolute -top-10 -right-10 w-32 sm:w-40 h-32 sm:h-40 bg-[#F7941D]/20 rounded-full blur-[50px] sm:blur-[60px]" />
            <div className="absolute top-0 right-16 w-32 h-[2px] bg-gradient-to-r from-transparent via-[#F7941D] to-transparent" />

            <div className="space-y-5 sm:space-y-6 relative z-10">
              <h3 className="text-base sm:text-xl font-black flex items-center gap-3 tracking-tight text-white">
                <div className="w-8 h-8 rounded-lg bg-[#0D3F7F]/60 border border-[#1E5CB3]/30 flex items-center justify-center text-[#F7941D] shrink-0">
                  <Calculator className="w-4 h-4" />
                </div>
                ROI Evaluation Report
              </h3>

              {/* KPI CARDS BLOCKS */}
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 sm:gap-4">
                <div className="p-3.5 sm:p-4 bg-white/5 rounded-xl border border-white/10 flex items-center justify-between group hover:border-[#F7941D]/40 transition-colors">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-slate-400 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider mb-1">
                      <Zap className="w-3.5 h-3.5 text-[#F7941D] shrink-0" /> Plant Size
                    </div>
                    <div className="text-lg sm:text-xl md:text-2xl font-black text-white tracking-tight">{metrics.recommendedCapacity} kW</div>
                  </div>
                </div>

                <div className="p-3.5 sm:p-4 bg-white/5 rounded-xl border border-white/10 flex items-center justify-between group hover:border-[#FFC107]/40 transition-colors">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-slate-400 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider mb-1">
                      <TrendingUp className="w-3.5 h-3.5 text-[#FFC107] shrink-0" /> Annual Yield
                    </div>
                    <div className="text-lg sm:text-xl md:text-2xl font-black text-[#FFC107] tracking-tight">{metrics.annualGeneration.toLocaleString()} kWh</div>
                  </div>
                </div>
              </div>

              {/* COMPREHENSIVE COST & SUBSIDY LEDGER */}
              <div className="space-y-3 sm:space-y-3.5 pt-1 sm:pt-2">
                <div className="flex justify-between items-center text-xs sm:text-sm gap-2">
                  <span className="text-slate-400 font-medium">Gross Infrastructure Cost</span>
                  <span className="font-bold text-white whitespace-nowrap">{formatIndianCurrency(metrics.grossCost)}</span>
                </div>

                {/* Highlighted Subsidy Row */}
                {propertyType === 'residential' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                    className="flex justify-between items-center text-xs sm:text-sm text-[#F7941D] font-bold bg-[#F7941D]/10 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border border-[#F7941D]/20 gap-2"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Landmark className="w-4 h-4 shrink-0" /> 
                      <span className="truncate">PM Surya Ghar Subsidy</span>
                    </span>
                    <span className="whitespace-nowrap">- {formatIndianCurrency(metrics.subsidyAmount)}</span>
                  </motion.div>
                )}

                <div className="flex justify-between items-center text-[13px] sm:text-[15px] border-b border-white/10 pb-3 sm:pb-4 pt-1 gap-2">
                  <span className="text-slate-300 font-medium">Net Capital Investment</span>
                  <span className="text-lg sm:text-xl md:text-2xl font-black text-white whitespace-nowrap">{formatIndianCurrency(metrics.netInvestment)}</span>
                </div>

                {/* SAVINGS PARAMS ROW */}
                <div className="grid grid-cols-2 gap-3 sm:gap-4 pt-2">
                  <div className="bg-[#0D3F7F]/20 p-3 rounded-lg border border-[#1E5CB3]/20">
                    <span className="text-[9px] sm:text-[10px] text-slate-400 font-bold uppercase tracking-wider block mb-0.5">Monthly Savings</span>
                    <span className="text-sm sm:text-lg font-black text-white block">{formatIndianCurrency(metrics.monthlySavings)}</span>
                  </div>
                  <div className="bg-[#0D3F7F]/20 p-3 rounded-lg border border-[#1E5CB3]/20">
                    <span className="text-[9px] sm:text-[10px] text-slate-400 font-bold uppercase tracking-wider block mb-0.5">Annual Retention</span>
                    <span className="text-sm sm:text-lg font-black text-[#FFC107] block">{formatIndianCurrency(metrics.annualSavings)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* LOWER LIFETIME PREDICTION BANNER BOX */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gradient-to-r from-[#F7941D]/20 to-transparent rounded-xl border border-[#F7941D]/20 mt-5 sm:mt-6 gap-3 sm:gap-3 z-10 relative">
              <div className="space-y-1 min-w-0">
                <span className="text-xs sm:text-[13px] font-black text-white tracking-wide flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-[#F7941D] shrink-0" /> 25-Year Earnings
                </span>
                <span className="text-[10px] sm:text-[11px] text-slate-400 font-medium block">Net lifecycle savings post payback</span>
              </div>
              <div className="w-full sm:w-auto flex sm:flex-col justify-between items-center sm:items-end gap-2 sm:gap-1 border-t sm:border-t-0 border-white/10 pt-3 sm:pt-0">
                <span className="text-lg sm:text-xl md:text-2xl font-black text-[#F7941D] whitespace-nowrap">{formatIndianCurrency(metrics.lifetimeSavings)}</span>
                <span className="text-[10px] sm:text-[11px] text-[#FFC107] font-bold bg-[#FFC107]/10 px-2 py-0.5 rounded border border-[#FFC107]/20 whitespace-nowrap">
                  Payback: {metrics.paybackPeriod} Yrs
                </span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>

      {/* Internal Custom Style for range slider thumb - Responsive sizes */}
      <style dangerouslySetInnerHTML={{__html: `
        input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #F7941D;
          cursor: pointer;
          box-shadow: 0 0 10px rgba(247,148,29,0.5);
          border: 2px solid white;
        }
        input[type=range]::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #F7941D;
          cursor: pointer;
          box-shadow: 0 0 10px rgba(247,148,29,0.5);
          border: 2px solid white;
        }
        @media (min-width: 640px) {
          input[type=range]::-webkit-slider-thumb {
            width: 18px;
            height: 18px;
          }
          input[type=range]::-moz-range-thumb {
            width: 18px;
            height: 18px;
          }
        }
      `}} />
    </section>
  );
};

export default SolarCalculator;